import { Checkbox, Form, Row } from 'antd';
import React, { useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { useDateLimit } from 'hooks/statniSprava/useDateLimit';
import { useFormBasics } from 'hooks/statniSprava/useFormBasics';
import usePageName from 'hooks/usePageName';

import { EZpusobPrevzati } from 'services/dokumenty/prikazy';

import { kategoriePozadavkySelect } from 'data/codebooks/statniSprava/pozadavkyKategorieSelect';

import isinValidation from 'utils/validations/isinValidation';

import AlertComponent from './AlertComponent';
import IsinSelect from './IsinSelect';
import IsinTable from './IsinTable';
import OdeslaniPozadavkuRows from './odeslaniPozadavkuRows';

interface IF45 {}

const F45: React.FC<IF45> = () => {
    const [form] = Form.useForm();
    const pageName = usePageName();
    const [, dateLoading, commonDisabledDate] = useDateLimit();
    const [showIsinTable, setShowIsinTable] = useState<boolean>(false);
    const [isDlt, setIsDlt] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);

    const [
        errMsg,
        currentStep,
        idSluzby,
        upozorneni,
        loading,
        odeslaniResp,
        setErrMsg,
        prev,
        onFormSubmit,
        showPxKlicLoadingMsg
    ] = useFormBasics();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const dataToSend = {
            ...fieldsValue,
            dtvp: fieldsValue.dtvp.format('YYYY-MM-DD'),
            zpusob: EZpusobPrevzati.MD,
            zast: !!fieldsValue.zast,
            esm: !!fieldsValue.esm,
            majucet: !!fieldsValue.majucet,
            bankucet: !!fieldsValue.bankucet
        };
        delete dataToSend.chkbxs;
        onFormSubmit(dataToSend);
    };

    useEffect(() => {
        form.setFieldsValue({
            zpusob: isDlt ? EZpusobPrevzati.MD : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDlt]);

    return (
        <LoadingWrapper loading={loading} loadingText={showPxKlicLoadingMsg ? 'LOADING_PXKLIC' : 'NACITANI'}>
            <Title level={4} text="DUVERNE_INFO_TITLE_F45" />
            {errMsg ? (
                <>
                    <Paragraph text={errMsg} />
                    <Row>
                        <ButtonComponent
                            id="f45ZpetBtn"
                            type="default"
                            buttontext="ZPET"
                            onClick={() => setErrMsg('')}
                        />
                    </Row>
                </>
            ) : (
                <>
                    {currentStep !== 2 ? (
                        <FormWrapper onFinish={onFinish} outForm={form}>
                            {showIsinTable ? (
                                <IsinTable upperForm={form} setShowIsinTable={setShowIsinTable} formIsinName={'cval'} />
                            ) : (
                                <>
                                    <LoadingWrapper loading={dateLoading}>
                                        <DatePickerComponent
                                            name="dtvp"
                                            disabled={currentStep > 0}
                                            label="ME_POZADAVKY_DATUM1_POZADOVANE"
                                            disabledDate={current => commonDisabledDate(current)}
                                        />
                                    </LoadingWrapper>

                                    {pageName === 'cnb' ? (
                                        <>
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="ME_POZADAVKY_CISLO_JEDNACI"
                                                name="actn"
                                                maxLength={30}
                                            />
                                            <InputComponent
                                                disabled={currentStep > 0}
                                                label="ISIN"
                                                name="cval"
                                                type="text"
                                                maxLength={12}
                                                rules={isinValidation()}
                                                capitalize={true}
                                            />
                                            {currentStep === 0 && (
                                                <ButtonComponent
                                                    id="f45IsinBtn"
                                                    buttontext="DUVERNE_INFO_ISIN_BUTTON"
                                                    onClick={() => setShowIsinTable(true)}
                                                    style={{
                                                        position: 'relative',
                                                        top: -12
                                                    }}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <IsinSelect
                                            disabled={currentStep > 0}
                                            setIsDlt={setIsDlt}
                                            onChange={() => rerender(pv => !pv)}
                                        />
                                    )}

                                    <SelectComponent
                                        disabled={currentStep > 0}
                                        name="pdcp"
                                        labelParagraph="ME_POZADAVKY_KATEGORIE"
                                        labelCol={{
                                            span: 8
                                        }}
                                        data={kategoriePozadavkySelect}
                                        defaultValue={kategoriePozadavkySelect[0].value}
                                        isFormItem={true}
                                    />

                                    <Form.Item name="chkbxs">
                                        <Row>
                                            <Form.Item
                                                style={{
                                                    fontWeight: 600
                                                }}
                                                name="zast"
                                                label={<TranslateText>F45_ZAST</TranslateText>}
                                                valuePropName="checked"
                                            >
                                                <Checkbox
                                                    style={{
                                                        position: 'relative',
                                                        top: -2,
                                                        left: 4
                                                    }}
                                                    onChange={() => rerender(pv => !pv)}
                                                    disabled={currentStep > 0}
                                                />
                                            </Form.Item>
                                        </Row>
                                        <Row>
                                            <Form.Item
                                                style={{
                                                    fontWeight: 600
                                                }}
                                                name="esm"
                                                label={<TranslateText>F45_ESM</TranslateText>}
                                                valuePropName="checked"
                                            >
                                                <Checkbox
                                                    style={{
                                                        position: 'relative',
                                                        top: -2,
                                                        left: 4
                                                    }}
                                                    onChange={() => rerender(pv => !pv)}
                                                    disabled={currentStep > 0}
                                                />
                                            </Form.Item>
                                        </Row>
                                        <Row>
                                            <Form.Item
                                                style={{
                                                    fontWeight: 600
                                                }}
                                                name="majucet"
                                                label={<TranslateText>F45_MAJUCET</TranslateText>}
                                                valuePropName="checked"
                                            >
                                                <Checkbox
                                                    style={{
                                                        position: 'relative',
                                                        top: -2,
                                                        left: 4
                                                    }}
                                                    onChange={() => rerender(pv => !pv)}
                                                    disabled={currentStep > 0}
                                                />
                                            </Form.Item>
                                        </Row>
                                        <Row>
                                            <Form.Item
                                                style={{
                                                    fontWeight: 600
                                                }}
                                                name="bankucet"
                                                label={<TranslateText>F45_BANKUCET</TranslateText>}
                                                valuePropName="checked"
                                            >
                                                <Checkbox
                                                    style={{
                                                        position: 'relative',
                                                        top: -2,
                                                        left: 4
                                                    }}
                                                    onChange={() => rerender(pv => !pv)}
                                                    disabled={currentStep > 0}
                                                />
                                            </Form.Item>
                                        </Row>
                                    </Form.Item>

                                    {currentStep === 0 && (
                                        <>
                                            <ButtonComponent
                                                id="f45OdeslatBtn"
                                                buttontext="ODESLAT"
                                                onClick={() => form.submit()}
                                            />
                                        </>
                                    )}
                                    {currentStep !== 0 && upozorneni && (
                                        <AlertComponent
                                            upozorneni={upozorneni}
                                            type={currentStep === 1 ? 'error' : 'info'}
                                        />
                                    )}
                                    {currentStep === 1 && (
                                        <Row className="button-row">
                                            <ButtonComponent
                                                id="f45ZpetAltBtn"
                                                buttontext="ZPET"
                                                onClick={() => prev()}
                                                type="default"
                                            />
                                            <ButtonComponent
                                                id="f45PotvrditBtn"
                                                buttontext="POTVRDIT"
                                                onClick={() => form.submit()}
                                            />
                                        </Row>
                                    )}
                                </>
                            )}
                        </FormWrapper>
                    ) : (
                        <>
                            <OdeslaniPozadavkuRows idSluzby={idSluzby} datum={odeslaniResp?.datum} />
                            {upozorneni && <AlertComponent upozorneni={upozorneni} type={'info'} />}
                        </>
                    )}
                </>
            )}
        </LoadingWrapper>
    );
};

export default F45;
